import React, { useState, useEffect } from 'react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Dashboard/Header/Header';
import {
  Search,
  SlidersHorizontal as Filters,
  MoreVertical,
  CalendarDays,
  X
} from 'lucide-react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import styles from './MonthlyReportList.module.css';
import Pagination from '../../../components/Common/Pagination/Pagination';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import Footer from '../../../components/Common/Footer/Footer';
import NoData from '../../../components/Common/NoData/NoData';
import { useLoading } from '../../../context/LoadingContext';

const MonthlyReportList = () => {
  const navigate = useNavigate();
  const { loading } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    department_id: '',
    designation_id: '',
    status: ''
  });
  const [filterData, setFilterData] = useState({
    departments: [],
    designations: []
  });

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 10
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      if (searchTerm !== debouncedSearch) {
        setPagination(prev => ({ ...prev, current_page: 1 }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleActionMenu = (id, e) => {
    if (openMenuIndex === id) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
      setOpenMenuIndex(id);
    }
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchFilterData = async () => {
    try {
      const [deptRes, desigRes] = await Promise.all([
        api.get('/departments'),
        api.get('/designations')
      ]);
      setFilterData({
        departments: deptRes.data.data.data || deptRes.data.data || [],
        designations: desigRes.data.data.data || desigRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, pagination.per_page, debouncedSearch, filters]);

  const fetchEmployees = async () => {
    try {
      let url = `/employees?page=${pagination.current_page}&per_page=${pagination.per_page}&search=${encodeURIComponent(debouncedSearch)}`;
      
      if (filters.department_id) url += `&department_id=${filters.department_id}`;
      if (filters.designation_id) url += `&designation_id=${filters.designation_id}`;
      if (filters.status !== '') url += `&status=${filters.status}`;

      const response = await api.get(url);
      if (response.data.success) {
        setEmployees(response.data.data.data);
        const { current_page, last_page, total, per_page } = response.data.data;
        setPagination({ current_page, last_page, total, per_page });
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const resetFilters = () => {
    setFilters({
      department_id: '',
      designation_id: '',
      status: ''
    });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = ['#fee2e2', '#fef3c7', '#dcfce7', '#d1fae5', '#e0f2fe', '#f3e8ff', '#fce7f3'];
    const charCodeSum = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return colors[charCodeSum % colors.length];
  };

  const getAvatarTextColor = (bgColor) => {
    return bgColor.replace('f', '9').replace('e', '7').replace('d', '5');
  };

  return (
    <div className={`${styles['employees-layout']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Monthly Reports</h1>
              <p>Select an employee to view their detailed monthly attendance report.</p>
            </div>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search by name, code, or department..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: pagination.per_page, label: `${pagination.per_page} per page` }}
                  onChange={(option) => setPagination(prev => ({ ...prev, per_page: option.value, current_page: 1 }))}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>
              <button 
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {(filters.department_id || filters.designation_id || filters.status !== '') && (
                  <span className={styles['filter-badge']}></span>
                )}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Employees</h3>
                  <p>Filter personnel by department, designation, or status.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Department</label>
                  <CustomSelect
                    value={filters.department_id ? { 
                      value: filters.department_id, 
                      label: filterData.departments.find(d => d.id === filters.department_id)?.departement_name 
                    } : null}
                    onChange={(opt) => handleFilterChange('department_id', opt ? opt.value : '')}
                    options={filterData.departments.map(d => ({ value: d.id, label: d.departement_name }))}
                    placeholder="All Departments"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Designation</label>
                  <CustomSelect
                    value={filters.designation_id ? { 
                      value: filters.designation_id, 
                      label: filterData.designations.find(d => d.id === filters.designation_id)?.designation_name 
                    } : null}
                    onChange={(opt) => handleFilterChange('designation_id', opt ? opt.value : '')}
                    options={filterData.designations.map(d => ({ value: d.id, label: d.designation_name }))}
                    placeholder="All Designations"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Status</label>
                  <CustomSelect
                    value={filters.status !== '' ? { 
                      value: filters.status, 
                      label: filters.status === '1' ? 'Active' : 'Inactive' 
                    } : null}
                    onChange={(opt) => handleFilterChange('status', opt ? opt.value : '')}
                    options={[
                      { value: '1', label: 'Active' },
                      { value: '0', label: 'Inactive' }
                    ]}
                    placeholder="All Status"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>Reset</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          <div className={styles['data-view-container']}>
            {/* Desktop View */}
            <div className={styles['table-wrapper']}>
              <table className={styles['employee-table']}>
                <thead>
                  <tr>
                    <th>EMPLOYEE & ID</th>
                    <th>DESIGNATION</th>
                    <th>DEPARTMENT</th>
                    <th>ROLE</th>
                    <th>STATUS</th>
                    <th className={styles['actions-cell']}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.length > 0 ? (
                    employees.map((employee) => (
                      <tr key={employee.id}>
                        <td className={styles['name-cell']}>
                          <div className={styles['user-info']}>
                            <div
                              className={styles['avatar']}
                              style={{
                                backgroundColor: getAvatarColor(employee.employee_name),
                                color: getAvatarTextColor(getAvatarColor(employee.employee_name))
                              }}
                            >
                              {getInitials(employee.employee_name)}
                            </div>
                            <div className={styles['name-code-wrapper']}>
                              <span className={styles['name-text']}>{employee.employee_name}</span>
                              <span className={styles['code-text']}>{employee.employee_code}</span>
                            </div>
                          </div>
                        </td>
                        <td>{employee.designation?.designation_name || 'N/A'}</td>
                        <td>{employee.department?.departement_name || 'N/A'}</td>
                        <td style={{ textTransform: 'capitalize' }}>{employee.role || 'N/A'}</td>
                        <td>
                          <span className={`${styles['status-pill']} ${employee.status === 1 ? styles['active'] : styles['inactive']}`}>
                            {employee.status === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className={styles['actions-cell']}>
                          <div className={`action-wrapper ${styles['action-wrapper']}`}>
                            <button
                              className={`${styles['action-btn']} ${openMenuIndex === employee.id ? styles['active'] : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleActionMenu(employee.id, e);
                              }}
                            >
                              <MoreVertical size={18} />
                            </button>

                            {openMenuIndex === employee.id && (
                              <div
                                className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                                style={{ top: menuPosition.top, left: menuPosition.left }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button 
                                  className={styles['dropdown-item']} 
                                  onClick={() => {
                                    setOpenMenuIndex(null);
                                    navigate(`/employees/monthly-report/view/${employee.id}`);
                                  }}
                                >
                                  <CalendarDays size={16} /> View Monthly Report
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : !loading && (
                    <NoData message="No employees found." colSpan={6} isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className={styles['employee-cards']}>
              {employees.length > 0 ? (
                employees.map((employee) => (
                  <div key={employee.id} className={styles['employee-card']}>
                    <div className={styles['card-main']}>
                      <div
                        className={styles['card-avatar']}
                        style={{
                          backgroundColor: getAvatarColor(employee.employee_name),
                          color: getAvatarTextColor(getAvatarColor(employee.employee_name))
                        }}
                      >
                        {getInitials(employee.employee_name)}
                      </div>
                      <div className={styles['card-info']}>
                        <span className={styles['card-name']}>{employee.employee_name}</span>
                        <span className={styles['card-code']}>{employee.employee_code}</span>
                      </div>
                      <div className={`action-wrapper ${styles['action-wrapper']}`}>
                        <button
                          className={`${styles['action-btn']} ${openMenuIndex === employee.id ? styles['active'] : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(employee.id, e);
                          }}
                        >
                          <MoreVertical size={18} />
                        </button>

                        {openMenuIndex === employee.id && (
                          <div
                            className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                            style={{ top: menuPosition.top, left: menuPosition.left }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button 
                              className={styles['dropdown-item']} 
                              onClick={() => {
                                setOpenMenuIndex(null);
                                navigate(`/employees/monthly-report/view/${employee.id}`);
                              }}
                            >
                              <CalendarDays size={16} /> View Monthly Report
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles['card-details']}>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Designation</span>
                        <span className={styles['detail-value']}>{employee.designation?.designation_name || 'N/A'}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Department</span>
                        <span className={styles['detail-value']}>{employee.department?.departement_name || 'N/A'}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Role</span>
                        <span className={styles['detail-value']} style={{ textTransform: 'capitalize' }}>{employee.role || 'N/A'}</span>
                      </div>
                    </div>
                    <div className={styles['card-footer']}>
                      <span className={`${styles['status-pill']} ${employee.status === 1 ? styles['active'] : styles['inactive']}`}>
                        {employee.status === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                ))
              ) : !loading && (
                <NoData message="No employees found." isTable={false} />
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={pagination.current_page}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
              />
            </div>
            <div className={styles['mobile-pagination']}>
              <Pagination
                currentPage={pagination.current_page}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
              />
            </div>
          </div>
        
          <Footer />
        </main>
      </div>

    </div>
  );
};

export default MonthlyReportList;
